/**
 * Which widget of the fixed set draws each request field
 * (04_frontend_spec.md §5.7), from the command's request schema: by type,
 * format and the `x-cis-field` hint, never by the field's name.
 *
 * Options that depend on the record (one disabled, with its reason) are not
 * here: they come from the dry run as a `Choice` (platform spec §4.4).
 *
 * A field the set cannot draw is reported as `unsupported` rather than
 * guessed at: the widget set is fixed (brief §9.1), so a command that needs
 * more is a decision, not a fallback.
 */
import {
  BULK_TARGETS_FIELD,
  PLATFORM_BODY_FIELDS,
  type ChoiceOption,
  type ChoiceValue,
  type CisField,
  type FieldHint,
  type JsonSchema,
  type ObjectId,
  type ShowIfValue,
  type UploadTarget,
} from "./contract";
import { getAt } from "./body-path";
import { FORMATTED_TEXT_HINT } from "../formatted-text";
import type { TagItemKind } from "./tag-list";
import { effectiveSchema, enumLabel, humanise, valueKindOf } from "./values";

interface Base {
  path: string;
  label: string;
  required: boolean;
  schema: JsonSchema;
  hint: CisField;
  /** The ghost text the dry run's field hint gives this record. */
  placeholder?: string;
  /** Shown only while each body path has the value given (or, for a list, any of them). */
  showIf: Record<string, ShowIfValue | readonly ShowIfValue[]> | null;
}

export type FieldDescriptor =
  | (Base & {
      kind: "text" | "textarea" | "number" | "money" | "checkbox" | "file";
    })
  /** Simple Markdown in a textarea; `formatHint` is the one line under it (not `hint`, which `Base` already holds). */
  | (Base & { kind: "formatted_text"; formatHint: string })
  | (Base & { kind: "date"; min: "today" | null })
  /** A date and a time of day (`format: date-time`): typed as local time, sent as the instant it names (`instantOfLocalDateTime`). */
  | (Base & { kind: "datetime" })
  | (Base & {
      kind: "select" | "radio";
      options: ChoiceOption[];
      /** What the picked option is sent as: a numeric enum's number, else the string. */
      valueType: ChoiceValueType;
    })
  /** Several values from a fixed list: an array of an enum, or of strings with `enum_labels` (lead-source categories). */
  | (Base & { kind: "multiselect"; options: ChoiceOption[] })
  /**
   * Several values typed in, no options: plain strings (I1's `product_keywords`) or numbers (E's Quantity options),
   * as chips.
   */
  | (Base & { kind: "taglist"; itemKind: TagItemKind })
  | (Base & {
      kind: "entity" | "suggest";
      object: ObjectId;
      /** An array of identifiers: the picker adds and removes several. Entity only. */
      multiple: boolean;
    })
  | (Base & { kind: "address"; object: ObjectId | null })
  /** A file uploaded first, the field taking the ref the upload answers (a supplier's quote document). */
  | (Base & {
      kind: "upload";
      upload: UploadTarget;
      accept: string[] | null;
      /** Per file. */
      maxBytes: number | null;
      /** A list of refs (`list[str]`): several files, each uploaded in turn. */
      multiple: boolean;
      /** The most files a several-files field takes (the schema's `maxItems`); null for no limit or one file. */
      maxFiles: number | null;
    })
  /**
   * Files of the command's own, one per slot the dry run names (`FieldHint.file_slots`: a repeatable item's parts).
   * The value is `{ [slot key]: ChosenFile }`; each file goes as a part of this field, its key as a part of `keysField`.
   */
  | (Base & { kind: "keyed_files"; keysField: string })
  | (Base & { kind: "table"; table: TableDescriptor })
  /** A nested object (Raise credit note's `bank_refund`): its own fields, drawn with the same widgets, at dotted paths. */
  | (Base & { kind: "group"; fields: FieldDescriptor[] })
  | (Base & { kind: "unsupported"; reason: string });

export interface TableColumn {
  field: string;
  label: string;
  schema: JsonSchema;
  editable: boolean;
  /** A boolean column is a tick per row (Release's `lines[].is_confirmed`); a date column is a date input (`format: date`). */
  kind: "number" | "money" | "text" | "boolean" | "date";
  /** A column of fixed values is a choice per row (Decide waiting orders…'s decision); null for any other. */
  options: ChoiceOption[] | null;
  /** The sibling row field holding this cell's per-row maximum. */
  maxFrom: string | null;
  /** The label of the link that fills this cell to its maximum. */
  fillLabel: string;
}

/**
 * An array of objects held inside each row of a table: `lines[].destinations[]`.
 * It is recognised by its shape (an array of objects among a row's properties),
 * not by a hint; `selectable` is read off the nested array's own `x-cis-field`.
 */
export interface NestedTableDescriptor {
  /** The row property holding the nested rows. */
  field: string;
  label: string;
  columns: TableColumn[];
  selectable: boolean;
}

export interface TableDescriptor {
  columns: TableColumn[];
  selectable: boolean;
  /**
   * The label of the control that adds a row, when the person adds and removes the rows themselves (a command's
   * request lines); null for a table whose rows the dry run supplies.
   */
  addLabel: string | null;
  /** A new row's values: each row property's schema default, else empty text, false or null by its type. */
  blankRow: Record<string, unknown>;
  /**
   * Nested row arrays, one set per parent row. ONE level is the limit: a
   * nested table's own array-of-object property makes the field `unsupported`
   * rather than being drawn, silently dropped or sent half-filled.
   */
  nested: NestedTableDescriptor[];
}

/** The default label of a table's fill link (Ben's call B5, provisional). */
export const DEFAULT_FILL_LABEL = "Max";

const reportedUntitled = new Set<string>();

function typeOf(schema: JsonSchema): string | undefined {
  const s = effectiveSchema(schema);
  return Array.isArray(s.type) ? s.type.find((t) => t !== "null") : s.type;
}

/**
 * A field's fixed values: its `enum`, or the one value of a one-value `Literal`, which arrives as `{"const": …}` with
 * no `enum`.
 */
function fixedValuesOf(schema: JsonSchema): string[] {
  const effective = effectiveSchema(schema);
  if (effective.enum) return effective.enum.map((value) => String(value));
  return typeof effective.const === "string" ? [effective.const] : [];
}

/** What a picked option is sent as: the schema's numbers as numbers (a VAT rate's `Literal[0, 5, 20]`), else strings. */
export type ChoiceValueType = "string" | "number";

/**
 * A dropdown holds strings, so a numeric enum's pick must go back to a number or the API refuses it
 * ("Input should be 0, 5 or 20" for `"20"`).
 */
function choiceValueTypeOf(schema: JsonSchema): ChoiceValueType {
  const effective = effectiveSchema(schema);
  const type = typeOf(effective);
  if (type === "integer" || type === "number") return "number";
  const values = effective.enum ?? [];
  return values.length > 0 && values.every((value) => typeof value === "number")
    ? "number"
    : "string";
}

/** The body value for a picked option, in the schema's own type. */
export function pickedChoiceValue(
  field: { valueType: ChoiceValueType },
  picked: string,
): string | number {
  return field.valueType === "number" ? Number(picked) : picked;
}

function enumOptions(schema: JsonSchema, hint: CisField): ChoiceOption[] {
  return optionsOf(fixedValuesOf(schema), hint);
}

function optionsOf(values: readonly string[], hint: CisField): ChoiceOption[] {
  return values.map((value) => ({
    value,
    label: enumLabel(hint.enum_labels, value),
    facts: null,
    is_default: false,
    why: null,
    disabled_reason: null,
  }));
}

/** At most this many options draw as a checkbox each; more draw as a searchable picker (provisional). */
export const MULTI_SELECT_CHECKBOX_LIMIT = 8;

/** How a multi-value list draws: a checkbox per option for a short list, a searchable picker for a long one. */
export function multiSelectModeOf(
  optionCount: number,
): "checkboxes" | "search" {
  return optionCount <= MULTI_SELECT_CHECKBOX_LIMIT ? "checkboxes" : "search";
}

/**
 * An array of strings drawn from a fixed list: the items' `enum`, or the field's `enum_labels` keys when the items
 * declare none. Null when neither names any value.
 */
function multiSelectOptionsOf(
  schema: JsonSchema,
  hint: CisField,
): ChoiceOption[] | null {
  const items = schema.items ? effectiveSchema(schema.items) : null;
  if (!items || typeOf(items) !== "string") return null;
  const values = items.enum
    ? items.enum.map((value) => String(value))
    : Object.keys(hint.enum_labels ?? {});
  return values.length > 0 ? optionsOf(values, hint) : null;
}

/**
 * What a typed-in list holds: plain strings (no enum, no format: a list of dates or files is not free text), whole
 * numbers or numbers. Null for anything else.
 */
function tagItemKindOf(schema: JsonSchema): TagItemKind | null {
  const items = schema.items ? effectiveSchema(schema.items) : null;
  if (!items || items.enum) return null;
  const type = typeOf(items);
  if (type === "integer") return "integer";
  if (type === "number") return "number";
  return type === "string" && items.format === undefined ? "text" : null;
}

/** Is this property an array of objects (a table's rows, or a table nested in a row)? */
function rowArrayOf(raw: JsonSchema): JsonSchema | null {
  const schema = effectiveSchema(raw);
  if (typeOf(schema) !== "array" || !schema.items) return null;
  return effectiveSchema(schema.items).properties ? schema : null;
}

/** A table row field marked `hidden`: its value travels with the row, but it is not shown. */
function isHidden(raw: JsonSchema): boolean {
  return effectiveSchema(raw)["x-cis-field"]?.hidden === true;
}

function columnOf(field: string, raw: JsonSchema): TableColumn {
  const column = effectiveSchema(raw);
  const columnHint = column["x-cis-field"] ?? {};
  const kind = valueKindOf(column);
  const fixedValues = (column.enum ?? []).filter(
    (value) => typeof value === "string",
  );
  return {
    field,
    label: column.title ?? humanise(field),
    schema: column,
    editable: column.readOnly !== true,
    kind:
      kind === "money" ||
      kind === "number" ||
      kind === "boolean" ||
      kind === "date"
        ? kind
        : "text",
    options: fixedValues.length > 0 ? enumOptions(column, columnHint) : null,
    maxFrom: columnHint.max_from ?? null,
    fillLabel: columnHint.fill_label ?? DEFAULT_FILL_LABEL,
  };
}

function blankRowOf(
  properties: Record<string, JsonSchema>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(properties).map(([field, raw]) => {
      const property = effectiveSchema(raw);
      if (property.default !== undefined) return [field, property.default];
      const type = typeOf(property);
      return [
        field,
        type === "string" ? "" : type === "boolean" ? false : null,
      ];
    }),
  );
}

function tableOf(
  schema: JsonSchema,
  hint: CisField,
): { table: TableDescriptor } | { reason: string } {
  const items = schema.items ? effectiveSchema(schema.items) : null;
  if (!items?.properties)
    return { reason: "a table needs an array of objects" };
  const columns: TableColumn[] = [];
  const nested: NestedTableDescriptor[] = [];
  for (const [field, raw] of Object.entries(items.properties)) {
    const nestedRows = rowArrayOf(raw);
    if (!nestedRows) {
      // A hidden key (an id) stays in the row and is sent back; it is never a column.
      if (!isHidden(raw)) columns.push(columnOf(field, raw));
      continue;
    }
    const nestedItems = effectiveSchema(nestedRows.items!);
    const nestedColumns: TableColumn[] = [];
    for (const [nestedField, nestedRaw] of Object.entries(
      nestedItems.properties!,
    )) {
      if (rowArrayOf(nestedRaw))
        return {
          reason: `a table nests one level only: ${field}.${nestedField} is a third level`,
        };
      if (!isHidden(nestedRaw))
        nestedColumns.push(columnOf(nestedField, nestedRaw));
    }
    nested.push({
      field,
      label: nestedRows.title ?? humanise(field),
      columns: nestedColumns,
      selectable: nestedRows["x-cis-field"]?.selectable === true,
    });
  }
  return {
    table: {
      columns,
      selectable: hint.selectable === true,
      addLabel: hint.add_rows ?? null,
      blankRow: blankRowOf(items.properties),
      nested,
    },
  };
}

/**
 * An entity picker: one identifier for a string, several for an array of
 * strings (the reshape's purchase orders). A suggestion is free text, so
 * only ever one.
 */
function entityOf(
  base: Base,
  kind: "entity" | "suggest",
  object: ObjectId,
  schema: JsonSchema,
): FieldDescriptor {
  if (typeOf(schema) !== "array")
    return { ...base, kind, object, multiple: false };
  const items = schema.items ? effectiveSchema(schema.items) : null;
  return kind === "entity" && items !== null && typeOf(items) === "string"
    ? { ...base, kind, object, multiple: true }
    : {
        ...base,
        kind: "unsupported",
        reason: `${kind} over an array needs an entity of strings`,
      };
}

/**
 * A nested object's fields, at `parent.child` paths. A field the set cannot draw (a table among them, too) makes the
 * whole object unsupported, so the shell reports it rather than sending the object half-filled.
 */
function groupOf(base: Base, schema: JsonSchema): FieldDescriptor {
  const required = new Set(schema.required ?? []);
  const fields: FieldDescriptor[] = [];
  for (const [name, property] of Object.entries(schema.properties!)) {
    const field = describe(
      `${base.path}.${name}`,
      property,
      required.has(name),
    );
    if (field.kind === "unsupported")
      return {
        ...base,
        kind: "unsupported",
        reason: `${field.path}: ${field.reason}`,
      };
    if (field.kind === "table")
      return {
        ...base,
        kind: "unsupported",
        reason: `${field.path}: a table inside an object field has no widget`,
      };
    fields.push(field);
  }
  return { ...base, kind: "group", fields };
}

/**
 * A discriminated union (Edit rule's `threshold`: immediately, after hours, after working days) as a group: one select
 * for the discriminator, and each variant's own fields shown while its value is the one chosen. The variants' fields
 * must have names of their own; the same name in two variants, or a field the set cannot draw, makes the whole union
 * unsupported.
 */
function unionGroupOf(base: Base, schema: JsonSchema): FieldDescriptor {
  const unsupported = (reason: string): FieldDescriptor => ({
    ...base,
    kind: "unsupported",
    reason,
  });
  const discriminator = schema.discriminator?.propertyName;
  if (!discriminator) return unsupported("oneOf schemas have no widget");
  const tagPath = `${base.path}.${discriminator}`;
  const tags: string[] = [];
  const fields: FieldDescriptor[] = [];
  let tagTitle: string | undefined;
  for (const raw of schema.oneOf ?? []) {
    const variant = effectiveSchema(raw);
    const tag = variant.properties?.[discriminator];
    const [value] = tag ? fixedValuesOf(tag) : [];
    if (value === undefined)
      return unsupported(
        `a oneOf variant has no single ${discriminator} value to choose it by`,
      );
    tags.push(value);
    tagTitle ??= tag?.title;
    const required = new Set(variant.required ?? []);
    for (const [name, property] of Object.entries(variant.properties ?? {})) {
      if (name === discriminator) continue;
      const field = describe(
        `${base.path}.${name}`,
        property,
        required.has(name),
      );
      if (field.kind === "unsupported" || field.kind === "table")
        return unsupported(`${field.path}: ${field.kind} in a oneOf variant`);
      if (fields.some((f) => f.path === field.path))
        return unsupported(`${field.path} is in two oneOf variants`);
      fields.push({
        ...field,
        showIf: { ...field.showIf, [tagPath]: value },
      });
    }
  }
  const chooser: FieldDescriptor = {
    ...base,
    path: tagPath,
    label: tagTitle ?? humanise(discriminator),
    showIf: null,
    kind: "select",
    valueType: "string",
    options: optionsOf(tags, base.hint),
  };
  return { ...base, kind: "group", fields: [chooser, ...fields] };
}

function describe(
  path: string,
  raw: JsonSchema,
  required: boolean,
): FieldDescriptor {
  const schema = effectiveSchema(raw);
  const hint = schema["x-cis-field"] ?? {};
  if (!schema.title && !reportedUntitled.has(path)) {
    reportedUntitled.add(path);
    console.warn("object_ui.field.untitled", { path });
  }
  const base: Base = {
    path,
    label: schema.title ?? humanise(path.split(".").pop() ?? path),
    required: required || hint.required === true,
    schema,
    hint,
    showIf: hint.show_if ?? null,
  };
  if (schema.oneOf) return unionGroupOf(base, schema);
  const type = typeOf(schema);
  if (hint.upload) {
    const refs = type === "array" && typeOf(schema.items ?? {}) === "string";
    return type === "string" || refs
      ? {
          ...base,
          kind: "upload",
          upload: hint.upload,
          accept: hint.accept ?? null,
          maxBytes: hint.max_bytes ?? null,
          multiple: refs,
          maxFiles: refs ? (schema.maxItems ?? null) : null,
        }
      : {
          ...base,
          kind: "unsupported",
          reason: "an upload field holds refs, strings: one, or a list of them",
        };
  }
  if (hint.keyed_by) {
    const items = effectiveSchema(schema.items ?? {});
    const isFileList =
      type === "array" &&
      typeOf(items) === "string" &&
      (items.contentMediaType !== undefined || items.format === "binary");
    return isFileList
      ? { ...base, kind: "keyed_files", keysField: hint.keyed_by }
      : {
          ...base,
          kind: "unsupported",
          reason: "keyed files are a list of files",
        };
  }
  const date = { ...base, kind: "date" as const, min: hint.min_date ?? null };
  switch (hint.widget) {
    case "textarea":
    case "checkbox":
    case "file":
    case "money":
    case "number":
    case "text":
      return { ...base, kind: hint.widget };
    case "formatted_text":
      return {
        ...base,
        kind: "formatted_text",
        formatHint: FORMATTED_TEXT_HINT,
      };
    case "date":
      return date;
    case "select":
    case "radio":
      return {
        ...base,
        kind: hint.widget,
        options: enumOptions(schema, hint),
        valueType: choiceValueTypeOf(schema),
      };
    case "entity":
    case "suggest": {
      const object = hint.widget === "entity" ? hint.entity : hint.suggestions;
      return object
        ? entityOf(base, hint.widget, object, schema)
        : {
            ...base,
            kind: "unsupported",
            reason: `${hint.widget} names no object`,
          };
    }
    case "address":
      return { ...base, kind: "address", object: hint.entity ?? null };
    case "table": {
      const built = tableOf(schema, hint);
      return "table" in built
        ? { ...base, kind: "table", table: built.table }
        : { ...base, kind: "unsupported", reason: built.reason };
    }
    default:
      break;
  }
  if (hint.entity) return entityOf(base, "entity", hint.entity, schema);
  if (hint.suggestions)
    return entityOf(base, "suggest", hint.suggestions, schema);
  if (fixedValuesOf(schema).length > 0)
    return {
      ...base,
      kind: "select",
      options: enumOptions(schema, hint),
      valueType: choiceValueTypeOf(schema),
    };
  if (type === "boolean") return { ...base, kind: "checkbox" };
  if (type === "integer" || type === "number")
    return { ...base, kind: "number" };
  if (type === "string" && schema.format === "date") return date;
  if (type === "string" && schema.format === "date-time")
    return { ...base, kind: "datetime" };
  if (type === "string" && schema.format === "binary")
    return { ...base, kind: "file" };
  if (type === "string") return { ...base, kind: "text" };
  if (type === "array") {
    const options = multiSelectOptionsOf(schema, hint);
    if (options) return { ...base, kind: "multiselect", options };
    const itemKind = tagItemKindOf(schema);
    if (itemKind) return { ...base, kind: "taglist", itemKind };
  }
  if (type === "object" && schema.properties) return groupOf(base, schema);
  return {
    ...base,
    kind: "unsupported",
    reason: `no widget for a ${type ?? "untyped"} field`,
  };
}

/**
 * The fields a command shows, in schema order. Left out: the platform's own
 * fields, the bulk targets, the guard's fields and the choices the dry run
 * offers (the shell fills those), any path a custom widget owns, and a field
 * the schema marks `hidden` (a choice's key, never drawn even before the
 * first dry run offers the choice).
 */
export function fieldsOf(
  schema: JsonSchema | null,
  options: { hidden: ReadonlySet<string> },
): FieldDescriptor[] {
  if (!schema?.properties) return [];
  const required = new Set(schema.required ?? []);
  return Object.entries(schema.properties).flatMap(([name, property]) => {
    if ((PLATFORM_BODY_FIELDS as readonly string[]).includes(name)) return [];
    if (name === BULK_TARGETS_FIELD || options.hidden.has(name)) return [];
    if (isHidden(property)) return [];
    return [describe(name, property, required.has(name))];
  });
}

/** Every field, a group's own fields after the group (depth first): what the body is built from. */
export function allFieldsOf(
  fields: readonly FieldDescriptor[],
): FieldDescriptor[] {
  return fields.flatMap((field) =>
    field.kind === "group" ? [field, ...allFieldsOf(field.fields)] : [field],
  );
}

/**
 * The fields as this record's dry run hints make them: a `placeholder` is the field's ghost text, `required` makes an
 * optional field required (no "(optional)" marker).
 */
export function withFieldHints(
  fields: readonly FieldDescriptor[],
  hints: Readonly<Record<string, FieldHint>>,
): FieldDescriptor[] {
  return fields.map((field) => {
    const hint = hints[field.path];
    const hinted: FieldDescriptor = hint
      ? {
          ...field,
          ...(hint.placeholder ? { placeholder: hint.placeholder } : {}),
          ...(hint.required ? { required: true } : {}),
        }
      : field;
    return hinted.kind === "group"
      ? { ...hinted, fields: withFieldHints(hinted.fields, hints) }
      : hinted;
  });
}

/**
 * Reads a body path as a `show_if` sees it: a choice the dry run offers (Raise credit note's `refund`) is held apart
 * from the body until the commit, so its pick answers for its key.
 */
export function bodyReader(
  body: unknown,
  choices: Readonly<Record<string, ChoiceValue>>,
): (path: string) => unknown {
  return (path) =>
    Object.prototype.hasOwnProperty.call(choices, path)
      ? choices[path]
      : getAt(body, path);
}

/** Is this field shown, given the body as it stands? */
export function isShown(
  field: FieldDescriptor,
  read: (path: string) => unknown,
): boolean {
  if (!field.showIf) return true;
  return Object.entries(field.showIf).every(([path, wanted]) => {
    const value = read(path);
    return Array.isArray(wanted)
      ? (wanted as readonly unknown[]).includes(value)
      : value === wanted;
  });
}

/** The `x-cis-field` date bound, as a `YYYY-MM-DD` the date input takes. */
export function resolveDateBound(
  bound: "today" | null,
  today: Date,
): string | undefined {
  if (bound !== "today") return undefined;
  const y = today.getFullYear();
  const m = String(today.getMonth() + 1).padStart(2, "0");
  const d = String(today.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Does the dialog for these fields need room? A table input (request lines, rows to decide) or a registered custom
 * widget is laid out in columns, so its command opens wide; every other command stays compact. Read off what the
 * request schema holds, never off a list of commands.
 */
export function isWideCommand(
  fields: readonly FieldDescriptor[],
  hasCustomWidget: boolean,
): boolean {
  return hasCustomWidget || allFieldsOf(fields).some((f) => f.kind === "table");
}

/** At most this many options draw as radio cards without a search box; more get one (provisional). */
export const CHOICE_SEARCH_LIMIT = 8;

/** A disabled default option of a multiple choice is ticked and locked on: it stays in the pick and cannot be unticked. */
export function isLockedOn(
  option: Pick<ChoiceOption, "disabled_reason" | "is_default">,
): boolean {
  return option.disabled_reason !== null && option.is_default;
}

/** Does an option answer a search box: every word typed is somewhere in its label, reason or facts? */
export function optionMatchesSearch(
  option: Pick<ChoiceOption, "label" | "why" | "facts">,
  search: string,
): boolean {
  const words = search.toLowerCase().split(/\s+/).filter(Boolean);
  const text =
    `${option.label} ${option.why ?? ""} ${option.facts ?? ""}`.toLowerCase();
  return words.every((word) => text.includes(word));
}

/**
 * The value of every select that has only one option (a one-value Literal): it draws as read-only text, so the body
 * has to carry the value or the command would be sent without it.
 */
export function soleOptionValues(
  fields: readonly FieldDescriptor[],
): Record<string, string | number> {
  return Object.fromEntries(
    allFieldsOf(fields).flatMap((f) =>
      f.kind === "select" && f.options.length === 1
        ? [[f.path, pickedChoiceValue(f, f.options[0]!.value)]]
        : [],
    ),
  );
}
